/**
 * Quality BY REPORTING PERIOD — never titled "over time" or "history".
 *
 * `periods[]` is a profile across the fiscal years one run checked, not a
 * series of runs over time. Only the current run and a single previous run
 * exist per chain, so a line spanning many points here would be misread as a
 * quality history that does not exist. `TrendPill` is where the one real
 * before/after comparison lives.
 */

import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import ChartCard from '../charts/ChartCard';
import { FOCAL_COLOR, barRadius, gridProps, legendProps, xAxisProps, yAxisProps } from '../charts/chartTheme';
import type { ValidationPeriod } from '../../../types/dataQuality';
import { periodProfile } from './dqSelectors';
import { SEVERITY_TOKENS } from './dqTokens';

export interface PeriodProfileChartProps {
  periods: ValidationPeriod[] | null | undefined;
  onSelectYear?: (year: number) => void;
}

export default function PeriodProfileChart({ periods, onSelectYear }: PeriodProfileChartProps) {
  const rows = periodProfile(periods);
  if (rows.length === 0) return null;

  return (
    <ChartCard title="Quality by reporting period" subtitle="Each fiscal year this run checked">
      <div style={{ height: Math.max(200, rows.length * 8 + 180) }}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={rows} margin={{ top: 4, right: 12, bottom: 4, left: 0 }}>
            <CartesianGrid {...gridProps} vertical={false} />
            <XAxis dataKey="label" {...xAxisProps} />
            <YAxis yAxisId="counts" allowDecimals={false} {...yAxisProps} />
            <YAxis
              yAxisId="score"
              orientation="right"
              domain={[0, 1]}
              tick={yAxisProps.tick}
              tickLine={false}
              axisLine={false}
              width={36}
            />
            <RechartsTooltip
              cursor={{ fill: 'rgba(0,0,0,0.03)' }}
              contentStyle={{ fontSize: 12, borderRadius: 8 }}
              formatter={(value: number, name: string) =>
                name === 'Score' ? [value.toFixed(2), name] : [value, name]
              }
            />
            <Legend {...legendProps} />
            <Bar
              yAxisId="counts"
              dataKey="errors"
              name="Errors"
              stackId="findings"
              fill={SEVERITY_TOKENS.error.hex}
              radius={barRadius as unknown as [number, number, number, number]}
              cursor={onSelectYear ? 'pointer' : undefined}
              // Drill-down lives on the BARS, not the whole plot: a handler on
              // the chart fired for clicks on empty space above a clean year
              // and on the score line, navigating on what felt like a misclick.
              onClick={(entry: unknown) => {
                const year = (entry as ValidationPeriod | undefined)?.year;
                if (year != null) onSelectYear?.(year);
              }}
            />
            <Bar
              yAxisId="counts"
              dataKey="warnings"
              name="Warnings"
              stackId="findings"
              fill={SEVERITY_TOKENS.warning.hex}
              radius={barRadius as unknown as [number, number, number, number]}
              cursor={onSelectYear ? 'pointer' : undefined}
              // Drill-down lives on the BARS, not the whole plot: a handler on
              // the chart fired for clicks on empty space above a clean year
              // and on the score line, navigating on what felt like a misclick.
              onClick={(entry: unknown) => {
                const year = (entry as ValidationPeriod | undefined)?.year;
                if (year != null) onSelectYear?.(year);
              }}
            />
            <Line
              yAxisId="score"
              type="monotone"
              dataKey="score"
              name="Score"
              stroke={FOCAL_COLOR}
              strokeWidth={2}
              dot={{ r: 3, fill: FOCAL_COLOR }}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </ChartCard>
  );
}
